import { useState, useEffect, useCallback } from "react";
import './Qhse.css';
import Footer from '../Component/NewFooter';
import SplashScreen from '../Component/SplashScreen';
import axios from "axios";

const BASE_URL      = "https://api.jeyshawnng.com";
const FETCH_TIMEOUT = 8000;
const CACHE_KEY      = "jeyshawn_qhse_data";

/* ── Helpers ───────────────────────────────────────────────────── */
function parseQhseData(db) {
  return {
    bannerImage: db?.bannerImage || "",
    title:       db?.title       || "",
    intro:       db?.intro       || "",
    quote:       db?.quote       || "",
    subIntro:    db?.subIntro    || "",
    policyIntro: db?.policyIntro || "",
    cards: Array.isArray(db?.sections)
      ? db.sections.map((sec, idx) => ({
          title:      sec?.heading || `Card ${idx + 1}`,
          paragraphs: Array.isArray(sec?.items) ? sec.items : [],
        }))
      : [],
  };
}

function readCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)); } catch { return null; }
}
function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
}

/* ── Sub-components ────────────────────────────────────────────── */
function ErrorScreen({ onRetry }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#09090b", zIndex: 9999,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px",
    }}>
      <div style={{ textAlign: "center" }}>
        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "1.1rem", fontFamily: "Georgia, serif", letterSpacing: "0.04em", marginBottom: "8px" }}>
          Poor internet connection.
        </p>
        <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.8rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Please check your connection and try again.
        </p>
      </div>
      <button
        onClick={onRetry}
        style={{
          marginTop: "8px", padding: "10px 32px", background: "transparent",
          border: "1px solid rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.7)",
          fontSize: "0.8rem", letterSpacing: "0.12em", textTransform: "uppercase",
          cursor: "pointer", fontFamily: "Georgia, serif", transition: "border-color 0.2s, color 0.2s",
        }}
        onMouseEnter={e => { e.target.style.borderColor = "#d4af72"; e.target.style.color = "#d4af72"; }}
        onMouseLeave={e => { e.target.style.borderColor = "rgba(255,255,255,0.25)"; e.target.style.color = "rgba(255,255,255,0.7)"; }}
      >
        Try Again
      </button>
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────── */
const QHSE = () => {
  const cached = readCache();

  const [status, setStatus]     = useState(cached ? "ready" : "splash-loading");
  const [pageData, setPageData] = useState(cached || null);

  const doFetch = useCallback(async (background = false) => {
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    try {
      const { data } = await axios.get(`${BASE_URL}/api/qhse`, { signal: controller.signal });
      clearTimeout(timeoutId);

      const parsed = parseQhseData(data?.data);
      writeCache(parsed);
      setPageData(parsed);

      if (!background) setStatus("splash-done");
    } catch (err) {
      clearTimeout(timeoutId);
      if (background) {
        console.warn("Background refresh failed for /api/qhse:", err.message);
      } else {
        setStatus("error");
      }
    }
  }, []);

  useEffect(() => {
    doFetch(!!cached);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "error") {
    return <ErrorScreen onRetry={() => { setStatus("splash-loading"); doFetch(false); }} />;
  }
  if (status === "splash-loading") return <SplashScreen blocking />;

  const data = pageData;

  return (
    <>
      {status === "splash-done" && (
        <SplashScreen autoExit onDone={() => setStatus("ready")} />
      )}

      <div className="img_carrier">
        {data.bannerImage && <img src={data.bannerImage} alt="" />}
      </div>

      <section className="qhse-section" aria-labelledby="qhse-title">
        <div className="qhse-section__header">
          <h2 className="qhse-section__title" id="qhse-title">
            {data.title}
          </h2>
          <hr className="qhse-section__divider" />
        </div>

        <p className="qhse-section__intro">{data.intro}</p>

        <div className="qhse-section__quote-box">
          <p className="qhse-section__quote">{data.quote}</p>
        </div>

        <p className="qhse-section__sub-intro">{data.subIntro}</p>

        <p className="qhse-section__policy-intro">{data.policyIntro}</p>

        <div className="qhse-section__cards">
          {data.cards.map((card, idx) => (
            <div className="qhse-card" key={card.title || idx}>
              <h3 className="qhse-card__title">{card.title}</h3>
              {card.paragraphs.map((para, i) => (
                <p className="qhse-card__text" key={i}>{para}</p>
              ))}
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </>
  );
};

export default QHSE;